import {
  useCreateCompanyMutation,
  useDeleteCompanyTypeMutation,
  useGetCompanyTypesQuery,
  useLazyGetThirdPartySuggestionsListQuery,
  useUpdateCompanyMutation,
} from '@/api'
import type { CompanyParams } from '@/api/models/company'
import { Autocomplete, Input } from '@/components'
import { useRole } from '@/hooks'
import {
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'
import { useCallback, useContext, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Accordion } from '../../components/Accordion'
import { OrganizationContext } from '../../Organization'
import { styles } from '../../styles'
import { GeneralData } from './components/GeneralData/GeneralData'
import { BankAccounts } from './modules/BankAccounts'
import { integration } from './types'

export const GeneralInfo = () => {
  const { errorForms, organization, refetchOrganization, organizationId } =
    useContext(OrganizationContext)
  const [payload, setPayload] = useState<CompanyParams>()
  const navigate = useNavigate()

  const [createCompany] = useCreateCompanyMutation()
  const [updateCompany] = useUpdateCompanyMutation()
  const [deleteCompanyType] = useDeleteCompanyTypeMutation()

  const [isFormReady, setFormReady] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getFormReady = (value?: any) => {
    if (organization) {
      if (value) {
        if (!isFormReady) {
          if (!value) {
            return false
          } else {
            setFormReady(true)
            return false
          }
        }
        return true
      } else {
        return isFormReady
      }
    } else {
      if (value) {
        setFormReady(true)
        return Boolean(value)
      } else {
        return isFormReady
      }
    }
  }

  const [getCompanies, { data: companies }] =
    useLazyGetThirdPartySuggestionsListQuery()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [companiesOptions, setCompaniesOptions] = useState<any[]>([])

  const companiesAutocomplateHandle = async (value: string) => {
    if (value) {
      const response = await getCompanies(value)
      if (response?.data && response?.data?.suggestions?.length > 0) {
        const options = response?.data?.suggestions?.map((item) => ({
          label: item.data?.name?.short || item?.value || '',
          value: item.data?.ogrn || '',
          status: item.data?.state?.status || '',
          short: item.data?.opf?.short || '',
          inn: item.data?.inn || '',
          address: item.data?.address?.value || '',
        }))
        setCompaniesOptions(options)
      }
    } else {
      setCompaniesOptions([])
    }
  }

  const { data: companyTypes } = useGetCompanyTypesQuery({
    query: JSON.stringify({ page: 1, per_page: 50 }),
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const companyTypeOptions = companyTypes?.data?.map((item: any) => ({
    label: item.name,
    value: item.id,
  }))
  const defaultCompanyTypeId = companyTypes?.data.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => e.name === 'Клиент'
  )?.id

  const defaults = useMemo(
    () => ({
      company: organization
        ? {
            label: organization?.short_title,
            value: organization?.id,
          }
        : undefined,
      phone_number: organization?.phone_number,
      legal_address: organization?.legal_address,
      phys_address: organization?.phys_address,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      companyType:
        organization?.companyTypes.length > 0
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            organization?.companyTypes?.map((item: any) => ({
              label: item.companyType.name,
              value: item.companyType.id,
            }))
          : [],
      signatureSystemType: organization?.signatureSystemType
        ? integration.find(
            (e) => e.value === organization?.signatureSystemType[0]
          )
        : undefined,
    }),
    [organization]
  )

  const generalDataForm = useForm()
  const watchGeneralData = generalDataForm.watch([
    'company',
    'phone_number',
    'legal_address',
    'phys_address',
    'companyType',
    'signatureSystemType',
  ])
  useMemo(() => {
    generalDataForm.reset(defaults)
  }, [defaults])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [typingTimer, setTypingTimer] = useState<any>()
  const typingListener = (
    inputValue: string,
    typingPause: number,
    callback: () => void
  ) => {
    clearTimeout(typingTimer)
    setTypingTimer(setTimeout(callback, typingPause))
  }

  useMemo(async () => {
    if (!getFormReady()) return
    typingListener(String(payload), 1800, async () => {
      if (payload?.companyType) {
        const orgIds = organization?.companyTypes?.map(
          (item: { companyType: { id: number } }) => item.companyType.id
        )
        const payIds = payload?.companyType?.map(
          (item: { companyTypeId: number }) => item.companyTypeId
        )
        const newIds =
          orgIds.length > 0
            ? payIds?.filter((id: number) => !orgIds?.includes(id))
            : payIds
        const delIds =
          payIds.length > 0
            ? orgIds?.filter((id: number) => !payIds.includes(id))
            : orgIds
        if (newIds.length > 0) {
          payload.companyType = newIds.map((id) => ({ companyTypeId: id }))
        } else {
          delete payload.companyType
        }
        if (delIds.length > 0) {
          await Promise.all(
            delIds.map(async (id: number) => {
              deleteCompanyType({
                companyId: organization?.id,
                companyTypeId: id,
              })
            })
          )
        }
      }
      if (payload) {
        if (!organization?.id) {
          await createCompany({
            title: payload.title,
            short_title: payload.short_title,
            inn: payload.inn,
            kpp: payload.kpp,
            ogrn: payload.ogrn,
            phone_number: payload.phone_number,
            legal_address: payload.legal_address,
            phys_address: payload.phys_address,
            companyType: defaultCompanyTypeId
              ? [{ companyTypeId: defaultCompanyTypeId }]
              : [],
            status: payload.status,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }).then((response: any) => {
            if (response?.error?.data) {
              if (response?.error?.data?.statusCode === 409) {
                generalDataForm.setError('company', {
                  message: response?.error?.data?.message,
                })
              } else {
                alert(
                  response?.error?.data?.message ||
                    `Ошибка при создании организации`
                )
              }
            } else {
              navigate(`/organization/${response?.data?.id}/general`, {
                replace: true,
              })
              refetchOrganization()
            }
          })
        } else {
          await updateCompany({
            id: organization?.id,
            payload: { ...payload },
          }).then(() => {
            setPayload(undefined)
            refetchOrganization()
          })
        }
      }
    })
  }, [payload])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[0])) return
    if (watchGeneralData[0]) {
      const company = companies?.suggestions.find(
        (item) => watchGeneralData[0]?.value === item.data.ogrn
      )?.data
      if (company) {
        setPayload({
          title: company.name.full_with_opf,
          short_title: company.name.short_with_opf,
          inn: company.inn,
          kpp: company.kpp,
          ogrn: company.ogrn,
          phones: company.phones,
          legal_address: company.address.value,
          phys_address: company.address.value,
          status: company.state.status,
        } as CompanyParams)
      }
    }
  }, [watchGeneralData[0]])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[1])) return
    if (
      watchGeneralData[1] &&
      watchGeneralData[1] !== organization?.phone_number
    ) {
      setPayload(
        (prev) =>
          ({
            ...prev,
            phone_number: watchGeneralData[1],
          }) as CompanyParams
      )
    } else {
      setPayload((prev) => {
        const res = { ...prev }
        delete res.phone_number
        return res as CompanyParams
      })
      generalDataForm.setValue('phone_number', '')
    }
  }, [watchGeneralData[1]])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[2])) return
    if (
      watchGeneralData[2] &&
      watchGeneralData[2] !== organization?.legal_address
    ) {
      setPayload(
        (prev) =>
          ({
            ...prev,
            legal_address: watchGeneralData[2],
          }) as CompanyParams
      )
    } else {
      setPayload((prev) => {
        const res = { ...prev }
        delete res.legal_address
        return res as CompanyParams
      })
      generalDataForm.setValue('legal_address', '')
    }
  }, [watchGeneralData[2]])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[3])) return
    if (
      watchGeneralData[3] &&
      watchGeneralData[3] !== organization?.phys_address
    ) {
      setPayload(
        (prev) =>
          ({
            ...prev,
            phys_address: watchGeneralData[3],
          }) as CompanyParams
      )
    } else {
      setPayload((prev) => {
        const res = { ...prev }
        delete res.phys_address
        return res as CompanyParams
      })
      generalDataForm.setValue('phys_address', '')
    }
  }, [watchGeneralData[3]])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[4])) return
    if (watchGeneralData[4]) {
      setPayload(
        (prev) =>
          ({
            ...prev,
            companyType: watchGeneralData[4].map(
              (item: { label: string; value: number | string }) => ({
                // companyId: Number(organizationId),
                companyTypeId: item.value,
              })
            ),
          }) as CompanyParams
      )
    } else {
      setPayload((prev) => {
        const res = { ...prev }
        //delete res.companyType
        res.companyType = []
        return res as CompanyParams
      })
      generalDataForm.setValue('companyType', [])
    }
  }, [watchGeneralData[4]])

  useMemo(() => {
    if (!getFormReady(watchGeneralData[5])) return
    if (watchGeneralData[5]) {
      setPayload(
        (prev) =>
          ({
            ...prev,
            signatureSystemType: watchGeneralData[5].value,
          }) as CompanyParams
      )
    } else {
      setPayload((prev) => {
        const res = { ...prev }
        delete res.signatureSystemType
        return res as CompanyParams
      })
      generalDataForm.setValue('signatureSystemType', '')
    }
  }, [watchGeneralData[5]])

  const { access } = useRole('ORGANIZATION', ['UPDATE', 'ADMIN'])

  return (
    <Stack gap={1}>
      <FormProvider {...generalDataForm}>
        <TableContainer>
          <Table sx={styles.table}>
            <TableBody>
              <TableRow
                sx={{
                  backgroundColor: '#EEEEEE',
                  height: '100%',
                }}
              >
                <TableCell
                  width={'30%'}
                  sx={{
                    verticalAlign: 'middle',
                    borderRadius: '4px',
                    fontWeight: 500,
                  }}
                >
                  Типы компаний
                </TableCell>
                <TableCell>
                  {access ? (
                    <Autocomplete
                      size="small"
                      multiple
                      limitTags={3}
                      id="companyType"
                      name="companyType"
                      options={companyTypeOptions || []}
                      label=""
                      placeholder={
                        organizationId
                          ? 'Выберите один или несколько типов'
                          : 'Сначала заполните «Общие данные»'
                      }
                      filterOptions={(options) => options}
                      readOnly={!access || !organizationId}
                    />
                  ) : (
                    <Stack direction={'row'} gap={'4px'}>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {organization?.companyTypes?.map((item: any) => (
                        <Chip
                          sx={{
                            backgroundColor: '#009FDA',
                            borderRadius: '4px',
                            color: 'white',
                            marginRight: '4px',
                          }}
                          key={item.companyType.id}
                          label={item.companyType.name}
                        />
                      ))}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Accordion
          titleStyle={{ fontWeight: 500 }}
          title={'Общие данные'}
          status={
            <Chip
              sx={styles.chip}
              variant={'outlined'}
              color={!errorForms?.generalInfo ? 'success' : 'error'}
              label={
                !errorForms?.generalInfo ? 'Добавлены' : 'Данные отсутсвуют'
              }
            />
          }
          defaultExpanded
        >
          <TableContainer>
            <Table sx={styles.table}>
              <TableBody>
                {access && (
                  <TableRow
                    sx={{
                      backgroundColor: '#EEEEEE',
                      height: '100%',
                    }}
                  >
                    <TableCell
                      width={'30%'}
                      sx={{
                        verticalAlign: 'middle',
                      }}
                    >
                      Поиск организации
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        id={'company'}
                        size="small"
                        name={'company'}
                        filterOptions={(e) => e}
                        options={companiesOptions || []}
                        placeholder={'Укажите наименование организации или ИНН'}
                        onInputChange={(_, data) => {
                          companiesAutocomplateHandle(data)
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {!errorForms?.generalInfo && (
                  <GeneralData data={organization} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Accordion>
        <Accordion
          titleStyle={{ fontWeight: 500 }}
          title={'Адреса и телефоны'}
          status={
            <Chip
              sx={styles.chip}
              variant={'outlined'}
              color={
                !organization
                  ? 'error'
                  : !errorForms?.generalInfoAddress
                  ? 'success'
                  : 'error'
              }
              label={
                !organization
                  ? 'Выберите организацию'
                  : !errorForms?.generalInfoAddress
                  ? 'Добавлены'
                  : 'Не заполнено'
              }
            />
          }
          //disabled={!hasAddresses}
        >
          <TableContainer>
            <Table sx={styles.table}>
              <TableBody>
                {!errorForms?.generalInfo && (
                  <>
                    <TableRow>
                      <TableCell
                        width={'30%'}
                        sx={{
                          verticalAlign: 'middle',
                        }}
                      >
                        Номер телефона
                      </TableCell>
                      <TableCell>
                        {access ? (
                          <Input
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  style={{ marginTop: '0px' }}
                                >
                                  <p style={{ fontSize: '0.93rem' }}>+7</p>
                                </InputAdornment>
                              ),
                            }}
                            mask="(000) 000-00-00"
                            placeholder="(999) 999-99-99"
                            fullWidth
                            sx={{
                              input: {
                                paddingTop: '7px',
                              },
                            }}
                            name="phone_number"
                          />
                        ) : (
                          <>+7 {organization?.phone_number}</>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        width={'30%'}
                        sx={{
                          verticalAlign: 'middle',
                        }}
                      >
                        Юридический адрес
                      </TableCell>
                      <TableCell>
                        {access ? (
                          <Input
                            fullWidth
                            placeholder="Введите юридический адрес"
                            sx={{
                              input: {
                                paddingTop: '7px',
                              },
                            }}
                            name="legal_address"
                          />
                        ) : (
                          <>{organization?.legal_address}</>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        width={'30%'}
                        sx={{
                          verticalAlign: 'middle',
                        }}
                      >
                        Физический адрес
                      </TableCell>
                      <TableCell>
                        {access ? (
                          <Input
                            fullWidth
                            placeholder="Введите физический адрес"
                            sx={{
                              input: {
                                paddingTop: '7px',
                              },
                            }}
                            name="phys_address"
                          />
                        ) : (
                          <>{organization?.phys_address}</>
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Accordion>
        <Accordion
          titleStyle={{ fontWeight: 500 }}
          title={'Банковские счета'}
          status={
            <Chip
              sx={styles.chip}
              variant={'outlined'}
              color={
                !organization
                  ? 'error'
                  : !errorForms?.generalInfoBankAccount
                  ? 'success'
                  : 'error'
              }
              label={
                !organization
                  ? 'Выберите организацию'
                  : !errorForms?.generalInfoBankAccount
                  ? 'Заполнены'
                  : 'Не заполнено'
              }
            />
          }
        >
          <TableContainer>
            <Table>
              <TableBody>
                {!errorForms?.generalInfo && <BankAccounts />}
              </TableBody>
            </Table>
          </TableContainer>
        </Accordion>

        <Accordion
          titleStyle={{ fontWeight: 500 }}
          title={'Интеграции'}
          status={
            <Chip
              sx={styles.chip}
              variant={'outlined'}
              color={
                !organization
                  ? 'error'
                  : !errorForms?.generalInfoIntegration
                  ? 'success'
                  : 'error'
              }
              label={
                !organization
                  ? 'Выберите организацию'
                  : !errorForms?.generalInfoIntegration
                  ? 'Добавлены'
                  : 'Не заполнено'
              }
            />
          }
          //disabled={!hasAddresses}
        >
          <TableContainer>
            <Table sx={styles.table}>
              <TableBody>
                {!errorForms?.generalInfo && (
                  <>
                    <TableRow>
                      <TableCell
                        width={'30%'}
                        sx={{
                          verticalAlign: 'middle',
                        }}
                      >
                        Титулы
                      </TableCell>
                      <TableCell>
                        {access ? (
                          <Autocomplete
                            id={'signatureSystemType'}
                            size="small"
                            name={'signatureSystemType'}
                            options={integration}
                            placeholder={'Выберите титул'}
                          />
                        ) : (
                          <>{organization?.signatureSystemType}</>
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Accordion>
      </FormProvider>
    </Stack>
  )
}
